import React, { useState, useLayoutEffect, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Image,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ActivityIndicator,
  SegmentedButtons,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import {
  useRecipe,
  useRecipeInteractions,
  useRecipeInteractionStatus,
} from '@coffeeplexity/api-client';
import { getFirstImageUrl } from '@coffeeplexity/shared';
import { Badge } from '@coffeeplexity/ui';
import { RecipeComments } from './RecipeComments';
import { HeaderActions } from '../ui/HeaderActions';
import { UserAvatar } from '../ui/UserAvatar';
import { useProfileNavigation } from '../../hooks/useProfileNavigation';
import {
  Clock,
  Users,
  Star,
  Thermometer,
  Scale,
  Timer,
  Heart,
  ChefHat,
  Gauge,
} from 'lucide-react-native';

interface RecipeDetailProps {
  recipeId: string;
}

// const { width } = Dimensions.get('window');

export function RecipeDetail({ recipeId }: RecipeDetailProps) {
  const theme = useTheme();
  const navigation = useNavigation();
  const { navigateToProfile } = useProfileNavigation();
  const { data: recipe, isLoading, error, refetch } = useRecipe(recipeId);
  const { like, unlike, bookmark, unbookmark, isLiking, isBookmarking } =
    useRecipeInteractions(recipeId);

  // Fetch interaction status from Redis (with DB fallback)
  const { data: interactionStatus, refetch: refetchInteractions } =
    useRecipeInteractionStatus(recipeId, !!recipeId);

  const [activeTab, setActiveTab] = useState('ingredients');

  // Initialize local interaction states from Redis/DB data
  const [isLikedLocal, setIsLikedLocal] = useState(
    interactionStatus?.is_liked || recipe?.is_liked || false
  );
  const [isBookmarkedLocal, setIsBookmarkedLocal] = useState(
    interactionStatus?.is_bookmarked || recipe?.is_bookmarked || false
  );

  // Sync with interaction status updates (Redis/DB) - PRIORITY SOURCE
  useEffect(() => {
    if (interactionStatus) {
      setIsLikedLocal(interactionStatus.is_liked);
      setIsBookmarkedLocal(interactionStatus.is_bookmarked);
    } else if (recipe) {
      // Fallback to recipe data if Redis data is not yet loaded
      if (recipe.is_liked !== undefined) setIsLikedLocal(recipe.is_liked);
      if (recipe.is_bookmarked !== undefined)
        setIsBookmarkedLocal(recipe.is_bookmarked);
    }
  }, [interactionStatus, recipe]);

  const [refreshing, setRefreshing] = useState(false);

  const handleLike = async () => {
    const newVal = !isLikedLocal;
    setIsLikedLocal(newVal);

    try {
      if (newVal) {
        await like();
      } else {
        await unlike();
      }
      // Refetch both recipe data and interaction status to ensure sync
      await Promise.all([refetch(), refetchInteractions()]);
    } catch (error) {
      // Revert on error
      setIsLikedLocal(!newVal);
      console.error('Failed to update like status:', error);
    }
  };

  const handleBookmark = async () => {
    const newVal = !isBookmarkedLocal;
    setIsBookmarkedLocal(newVal);

    try {
      if (newVal) {
        await bookmark();
      } else {
        await unbookmark();
      }
      // Refetch both recipe data and interaction status to ensure sync
      await Promise.all([refetch(), refetchInteractions()]);
    } catch (error) {
      // Revert on error
      setIsBookmarkedLocal(!newVal);
      console.error('Failed to update bookmark status:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refetch(), refetchInteractions()]);
    setRefreshing(false);
  };

  // Set up header actions
  useLayoutEffect(() => {
    if (recipe) {
      (navigation as any).setOptions({
        headerRight: () => (
          <HeaderActions
            isLiked={isLikedLocal}
            onLikePress={handleLike}
            isLiking={isLiking}
            isBookmarked={isBookmarkedLocal}
            onBookmarkPress={handleBookmark}
            isBookmarking={isBookmarking}
            shareContent={{
              title: recipe.title,
              message: `Check out this amazing coffee recipe: ${recipe.title}\n\n${recipe.description}`,
              url: `https://coffeeplexity.com/recipes/${recipe.id}`,
            }}
            testID='recipe-header-actions'
          />
        ),
      });
    }
  }, [
    navigation,
    recipe,
    isLikedLocal,
    isLiking,
    isBookmarkedLocal,
    isBookmarking,
  ]);

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size='large' color={theme.colors.primary} />
      </View>
    );
  }

  if (error || !recipe) {
    return (
      <View style={styles.centered}>
        <Text
          variant='titleMedium'
          style={[styles.errorText, { color: theme.colors.error }]}
        >
          Failed to load recipe
        </Text>
        <Button mode='contained' onPress={() => refetch()}>
          Try Again
        </Button>
      </View>
    );
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Recipe Header - Similar to Creator Profile Header */}
      <View style={[styles.header, { backgroundColor: theme.colors.surface }]}>
        <View style={styles.recipeInfo}>
          {/* Recipe Image */}
          {recipe.video_url ? (
            <View style={[styles.recipeImage, styles.videoPlaceholder]}>
              <ChefHat size={32} color='#fff' strokeWidth={1.5} />
              <Text variant='bodyMedium' style={styles.videoText}>
                Video Recipe
              </Text>
            </View>
          ) : (
            (() => {
              const firstImage = getFirstImageUrl(recipe.images);
              return firstImage ? (
                <Image
                  source={{ uri: firstImage }}
                  style={styles.recipeImage}
                  resizeMode='cover'
                />
              ) : (
                <View style={[styles.recipeImage, styles.imagePlaceholder]}>
                  <ChefHat
                    size={32}
                    color={theme.colors.onSurfaceVariant}
                    strokeWidth={1.5}
                  />
                </View>
              );
            })()
          )}

          {/* Recipe Title and Badges */}
          <View style={styles.recipeDetails}>
            <Text
              variant='headlineMedium'
              style={[styles.title, { flexShrink: 1, flexWrap: 'wrap' }]}
              numberOfLines={3}
            >
              {recipe.title}
            </Text>
            {/* Badges */}
            <View style={styles.badgesRow}>
              <Badge variant='brewing-method' style={styles.badge}>
                {recipe.brewing_method.replace(/_/g, ' ')}
              </Badge>
              <Badge variant='difficulty'>{recipe.difficulty}</Badge>
              {recipe.is_featured && <Badge variant='featured'>Featured</Badge>}
            </View>

            {/* Creator Info */}
            {recipe.creator && (
              <TouchableOpacity
                onPress={() =>
                  navigateToProfile(recipe.creator!.id, recipe.creator!.role)
                }
                style={styles.creatorRow}
              >
                <UserAvatar
                  size={40}
                  avatarUrl={recipe.creator.avatar_url}
                  name={recipe.creator.display_name}
                  firstName={recipe.creator.first_name}
                  lastName={recipe.creator.last_name}
                />
                <View style={styles.creatorText}>
                  <Text variant='bodyMedium'>
                    {recipe.creator.display_name}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    Recipe Creator
                  </Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Counts Row */}
        <View style={styles.lovedByContainer}>
          <View style={styles.countItem}>
            <Heart
              size={16}
              color={theme.colors.primary}
              fill={isLikedLocal ? theme.colors.primary : 'transparent'}
            />
            <Text variant='titleMedium' style={{ marginLeft: 8 }}>
              {formatCount(recipe.likes_count || recipe.favorite_count || 0)}{' '}
              likes
            </Text>
          </View>

          <View style={[styles.countItem, { marginLeft: 24 }]}>
            <Text variant='titleMedium'>
              {formatCount(recipe.bookmarks_count || 0)} bookmarks
            </Text>
          </View>
        </View>
      </View>

      {/* Description Card */}
      <Card
        style={[
          styles.descriptionCard,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Description
          </Text>
          <Text variant='bodyMedium' style={styles.description}>
            {recipe.description}
          </Text>
        </Card.Content>
      </Card>

      {/* Quick Stats Card */}
      <Card
        style={[styles.statsCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Quick Stats
          </Text>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                ]}
              >
                <Clock size={16} color={theme.colors.onSurfaceVariant} />
              </View>
              <Text variant='headlineSmall' style={styles.statValue}>
                {recipe.prep_time_minutes}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Prep (min)
              </Text>
            </View>

            <View style={styles.statItem}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                ]}
              >
                <Timer size={16} color={theme.colors.onSurfaceVariant} />
              </View>
              <Text variant='headlineSmall' style={styles.statValue}>
                {recipe.total_time_minutes}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Total (min)
              </Text>
            </View>

            <View style={styles.statItem}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                ]}
              >
                <Users size={16} color={theme.colors.onSurfaceVariant} />
              </View>
              <Text variant='headlineSmall' style={styles.statValue}>
                {recipe.servings}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Servings
              </Text>
            </View>

            <View style={styles.statItem}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                ]}
              >
                <Star size={16} color={theme.colors.onSurfaceVariant} />
              </View>
              <Text variant='headlineSmall' style={styles.statValue}>
                {recipe.rating?.toFixed(1) || 'N/A'}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Rating
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Brewing Parameters Card */}
      {recipe.brewing_parameters && (
        <Card
          style={[
            styles.parametersCard,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Brewing Parameters
            </Text>

            <View style={styles.parameterItem}>
              <View
                style={[
                  styles.parameterIconContainer,
                  { backgroundColor: 'rgba(244, 67, 54, 0.1)' },
                ]}
              >
                <Thermometer size={18} color='#F44336' />
              </View>
              <View style={styles.parameterText}>
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  Water Temperature
                </Text>
                <Text variant='titleMedium' style={styles.parameterValue}>
                  {recipe.brewing_parameters.water_temperature_celsius}°C
                </Text>
              </View>
            </View>

            <View style={styles.parameterItem}>
              <View
                style={[
                  styles.parameterIconContainer,
                  { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                ]}
              >
                <Scale size={18} color='#4CAF50' />
              </View>
              <View style={styles.parameterText}>
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  Coffee to Water Ratio
                </Text>
                <Text variant='titleMedium' style={styles.parameterValue}>
                  {recipe.brewing_parameters.coffee_to_water_ratio}
                </Text>
              </View>
            </View>

            <View style={styles.parameterItem}>
              <View
                style={[
                  styles.parameterIconContainer,
                  { backgroundColor: 'rgba(33, 150, 243, 0.1)' },
                ]}
              >
                <Timer size={18} color='#2196F3' />
              </View>
              <View style={styles.parameterText}>
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  Total Brew Time
                </Text>
                <Text variant='titleMedium' style={styles.parameterValue}>
                  {(() => {
                    const brewTime =
                      recipe.brewing_parameters?.total_brew_time_seconds;
                    if (brewTime === null || brewTime === undefined)
                      return 'N/A';
                    let numericBrewTime: number;
                    if (typeof brewTime === 'string') {
                      numericBrewTime = parseFloat(brewTime);
                      if (isNaN(numericBrewTime)) return 'N/A';
                    } else if (typeof brewTime === 'number') {
                      numericBrewTime = brewTime;
                    } else {
                      return 'N/A';
                    }
                    if (isNaN(numericBrewTime) || numericBrewTime < 0)
                      return 'N/A';
                    return formatTime(numericBrewTime);
                  })()}
                </Text>
              </View>
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Recipe Details Card with Tabs */}
      <Card
        style={[styles.detailsCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Recipe Details
          </Text>

          <SegmentedButtons
            value={activeTab}
            onValueChange={setActiveTab}
            buttons={[
              { value: 'ingredients', label: 'Ingredients' },
              { value: 'steps', label: 'Steps' },
              { value: 'equipment', label: 'Equipment' },
            ]}
            style={styles.tabs}
          />

          {activeTab === 'ingredients' && (
            <View style={styles.tabPanel}>
              {recipe.ingredients.map((ingredient, index) => (
                <View key={index} style={styles.ingredientRow}>
                  <Text variant='bodyMedium' style={styles.ingredientName}>
                    {ingredient.name}
                  </Text>
                  <Text
                    variant='bodyMedium'
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    {ingredient.amount} {ingredient.unit}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {activeTab === 'steps' && (
            <View style={styles.tabPanel}>
              {recipe.steps.map((step, index) => (
                <View key={index} style={styles.stepCard}>
                  <View style={styles.stepHeader}>
                    <View
                      style={[
                        styles.stepNumber,
                        { backgroundColor: theme.colors.primary },
                      ]}
                    >
                      <Text
                        variant='titleMedium'
                        style={{ color: theme.colors.onPrimary }}
                      >
                        {step.step_number}
                      </Text>
                    </View>
                    <Text variant='titleSmall' style={styles.stepTitle}>
                      {step.title}
                    </Text>
                  </View>

                  <Text
                    variant='bodyMedium'
                    style={[
                      styles.stepDescription,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    {step.description}
                  </Text>

                  {(step.duration_seconds || step.temperature_celsius) && (
                    <View style={styles.stepMeta}>
                      {step.duration_seconds && (
                        <View style={styles.stepMetaItem}>
                          <Timer size={14} color={theme.colors.primary} />
                          <Text
                            variant='bodySmall'
                            style={{
                              color: theme.colors.primary,
                              marginLeft: 4,
                            }}
                          >
                            {formatTime(step.duration_seconds)}
                          </Text>
                        </View>
                      )}
                      {step.temperature_celsius && (
                        <View style={styles.stepMetaItem}>
                          <Thermometer size={14} color='#F44336' />
                          <Text
                            variant='bodySmall'
                            style={{ color: '#F44336', marginLeft: 4 }}
                          >
                            {step.temperature_celsius}°C
                          </Text>
                        </View>
                      )}
                    </View>
                  )}

                  {step.notes && (
                    <View style={styles.stepNotesContainer}>
                      <Text
                        variant='bodySmall'
                        style={[
                          styles.stepNotes,
                          { color: theme.colors.onSurfaceVariant },
                        ]}
                      >
                        💡 {step.notes}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}

          {activeTab === 'equipment' && (
            <View style={styles.tabPanel}>
              {recipe.equipment?.map(item => (
                <View key={item.id} style={styles.equipmentCard}>
                  <View style={styles.equipmentHeader}>
                    <View
                      style={[
                        styles.equipmentIcon,
                        { backgroundColor: 'rgba(103, 80, 164, 0.1)' },
                      ]}
                    >
                      <Gauge size={18} color={theme.colors.primary} />
                    </View>
                    <View style={styles.equipmentInfo}>
                      <Text
                        variant='titleSmall'
                        style={{ color: theme.colors.onSurface }}
                      >
                        {item.name}
                      </Text>
                      <Text
                        variant='bodySmall'
                        style={{
                          color: theme.colors.onSurfaceVariant,
                          textTransform: 'capitalize',
                        }}
                      >
                        {item.type}
                      </Text>
                    </View>
                  </View>
                  {item.description && (
                    <Text
                      variant='bodySmall'
                      style={{
                        color: theme.colors.onSurfaceVariant,
                        marginTop: 8,
                      }}
                    >
                      {item.description}
                    </Text>
                  )}
                </View>
              )) || (
                <View style={styles.emptyState}>
                  <Gauge
                    size={32}
                    color={theme.colors.onSurfaceVariant}
                    strokeWidth={1}
                  />
                  <Text
                    variant='bodyMedium'
                    style={{
                      color: theme.colors.onSurfaceVariant,
                      marginTop: 12,
                      textAlign: 'center',
                    }}
                  >
                    No specific equipment requirements
                  </Text>
                </View>
              )}
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Tags Card */}
      {recipe.tags.length > 0 && (
        <Card
          style={[styles.tagsCard, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Tags
            </Text>
            <View style={styles.tagsWrapper}>
              {recipe.tags.map((tag, index) => (
                <View
                  key={index}
                  style={[styles.tag, { backgroundColor: '#f3f4f6' }]}
                >
                  <Text
                    style={{
                      color: '#374151',
                      fontSize: 12,
                      fontWeight: '500',
                      letterSpacing: 0.4,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                    }}
                  >
                    {tag.replace(/_/g, ' ')}
                  </Text>
                </View>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Comments Section */}
      <View style={styles.commentsSection}>
        <RecipeComments recipeId={recipeId} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
  },

  // Header Section (similar to CreatorProfileScreen)
  header: {
    padding: 16,
    paddingBottom: 24,
  },
  recipeInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  recipeImage: {
    width: '100%',
    height: 200,
    borderRadius: 16,
    marginBottom: 16,
  },
  videoPlaceholder: {
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholder: {
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoText: {
    color: '#fff',
    textAlign: 'center',
    marginTop: 8,
  },
  recipeDetails: {
    alignItems: 'center',
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  badgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
    justifyContent: 'center',
  },
  badge: {
    padding: 5,
  },
  creatorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  creatorText: {
    marginLeft: 12,
  },
  lovedByContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  countItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  // Card Sections
  descriptionCard: {
    margin: 16,
  },
  description: {
    lineHeight: 22,
    marginTop: 8,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },

  // Stats Card
  statsCard: {
    margin: 16,
    marginTop: 0,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontWeight: 'bold',
    marginTop: 4,
    marginBottom: 2,
  },
  statLabel: {
    opacity: 0.7,
    textAlign: 'center',
  },

  // Parameters Card
  parametersCard: {
    margin: 16,
    marginTop: 0,
  },
  parameterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  parameterIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  parameterText: {
    flex: 1,
  },
  parameterValue: {
    fontWeight: 'bold',
    marginTop: 4,
  },

  // Details Card with Tabs
  detailsCard: {
    margin: 16,
    marginTop: 0,
  },
  tabs: {
    marginBottom: 16,
  },
  tabPanel: {
    marginTop: 8,
  },

  // Ingredients
  ingredientRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  ingredientName: {
    flex: 1,
  },

  // Steps
  stepCard: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepTitle: {
    flex: 1,
    fontWeight: '600',
  },
  stepDescription: {
    lineHeight: 22,
    marginBottom: 8,
  },
  stepMeta: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  stepMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepNotesContainer: {
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  stepNotes: {
    fontStyle: 'italic',
    lineHeight: 18,
  },

  // Equipment
  equipmentCard: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  equipmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  equipmentIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  equipmentInfo: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },

  // Tags Card
  tagsCard: {
    margin: 16,
    marginTop: 0,
  },
  tagsWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },

  // Comments Section
  commentsSection: {
    marginTop: 8,
    marginBottom: 16,
    minHeight: 400,
  },
});
